import React, { useState } from "react";
import { Col, Row, Skeleton, Tooltip, Typography } from "antd";
import {
  TeamOutlined, CheckCircleOutlined, PauseCircleOutlined,
  QuestionCircleOutlined, LoginOutlined, FieldTimeOutlined,
  CalendarOutlined, ClockCircleOutlined,
  ApartmentOutlined, GlobalOutlined, CrownOutlined, UserDeleteOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// ── Tarjeta individual con hover ──────────────────────────────────────────

const StatCard = ({ title, value, icon, color, tooltip, loading }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Tooltip title={tooltip} placement="top" mouseEnterDelay={0.4}>
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        style={{
          background:   "#fff",
          borderRadius: 12,
          padding:      "16px 18px",
          borderTop:    `3px solid ${color}`,
          boxShadow:    hovered
            ? `0 8px 22px rgba(0,0,0,0.12), 0 2px 8px ${color}28`
            : "0 1px 5px rgba(0,0,0,0.08)",
          transform:    hovered ? "translateY(-3px)" : "translateY(0)",
          transition:   "all 0.22s cubic-bezier(0.34,1.56,0.64,1)",
          cursor:       "default",
          height:       "100%",
          userSelect:   "none",
          display:      "flex",
          alignItems:   "center",
          gap:          14,
        }}
      >
        {/* Icono */}
        <div
          style={{
            width: 44, height: 44, borderRadius: 10, flexShrink: 0,
            background: `${color}18`,
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "transform 0.2s ease",
            transform: hovered ? "scale(1.12)" : "scale(1)",
          }}
        >
          {icon
            ? React.cloneElement(icon, { style: { color, fontSize: 20 } })
            : null}
        </div>

        {/* Valor + etiqueta */}
        <Skeleton loading={loading} active paragraph={false} title={{ width: "55%" }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#141414", lineHeight: 1.2, letterSpacing: "-0.4px" }}>
              {typeof value === "number"
                ? value.toLocaleString("es-CO")
                : (value ?? "—")}
            </div>
            <Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 3, display: "block", fontWeight: 500, letterSpacing: "0.1px" }}
            >
              {title}
            </Text>
          </div>
        </Skeleton>
      </div>
    </Tooltip>
  );
};

// ── Datos de las 12 tarjetas en orden 6×2 ────────────────────────────────

const buildCards = (stats) => [
  // ── Fila 1: usuarios y entidades ──────────────────────────────────────
  {
    title:   "Total usuarios",
    value:   stats?.total_usuarios,
    icon:    <TeamOutlined />,
    color:   "#1677ff",
    tooltip: "Usuarios registrados en el sistema",
  },
  {
    title:   "Activos",
    value:   stats?.activos,
    icon:    <CheckCircleOutlined />,
    color:   "#389e0d",
    tooltip: "Usuarios con estado activo (active = Y)",
  },
  {
    title:   "Inactivos",
    value:   stats?.inactivos,
    icon:    <PauseCircleOutlined />,
    color:   "#d46b08",
    tooltip: "Usuarios con estado inactivo (active = N)",
  },
  {
    title:   "ART",
    value:   stats?.por_entidad?.ART,
    icon:    <ApartmentOutlined />,
    color:   "#0958d9",
    tooltip: "Usuarios registrados como entidad ART",
  },
  {
    title:   "FAO",
    value:   stats?.por_entidad?.FAO,
    icon:    <GlobalOutlined />,
    color:   "#087c7c",
    tooltip: "Usuarios registrados como entidad FAO",
  },
  {
    title:   "ADMIN",
    value:   stats?.por_entidad?.ADMIN,
    icon:    <CrownOutlined />,
    color:   "#531dab",
    tooltip: "Usuarios con perfil administrador",
  },
  // ── Fila 2: actividad reciente ────────────────────────────────────────
  {
    title:   "Sin estado",
    value:   stats?.sin_estado,
    icon:    <QuestionCircleOutlined />,
    color:   "#8c8c8c",
    tooltip: "Usuarios sin estado definido",
  },
  {
    title:   "Sin entidad",
    value:   stats?.por_entidad?.sin_entidad,
    icon:    <UserDeleteOutlined />,
    color:   "#8c8c8c",
    tooltip: "Usuarios sin entidad asignada",
  },
  {
    title:   "Ingresos hoy",
    value:   stats?.ingresos_hoy,
    icon:    <LoginOutlined />,
    color:   "#389e0d",
    tooltip: "Eventos de sesión registrados hoy (sec_user_token_hist)",
  },
  {
    title:   "Últimos 7 días",
    value:   stats?.ingresos_semana,
    icon:    <FieldTimeOutlined />,
    color:   "#1677ff",
    tooltip: "Eventos de sesión en los últimos 7 días",
  },
  {
    title:   "Últimos 30 días",
    value:   stats?.ingresos_mes,
    icon:    <CalendarOutlined />,
    color:   "#087c7c",
    tooltip: "Eventos de sesión en los últimos 30 días",
  },
  {
    title:   "Último ingreso",
    value:   stats?.ultimo_ingreso
      ? new Date(stats.ultimo_ingreso).toLocaleDateString("es-CO", {
          day: "2-digit", month: "short", year: "numeric",
        })
      : "—",
    icon:    <ClockCircleOutlined />,
    color:   "#595959",
    tooltip: stats?.ultimo_ingreso
      ? `Último evento: ${new Date(stats.ultimo_ingreso).toLocaleString("es-CO")}`
      : "Sin eventos registrados",
  },
];

// ── Componente principal ───────────────────────────────────────────────────

const StatsCards = ({ stats, loading }) => {
  const cards = buildCards(stats);

  return (
    <Row gutter={[16, 16]}>
      {cards.map((card) => (
        <Col
          key={card.title}
          xs={24}  // 1 por fila en móvil
          sm={12}  // 2 por fila en tablet pequeño
          md={6}   // 4 por fila en portátil y superior → layout 4 × 3
        >
          <StatCard {...card} loading={loading} />
        </Col>
      ))}
    </Row>
  );
};

export default StatsCards;
